import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import mockData from '../../mockData/incidents.json';
import {
  HiOutlineClipboardDocumentCheck,
  HiOutlineExclamationTriangle,
  HiOutlineShieldCheck,
  HiOutlineCurrencyDollar,
  HiOutlineUserGroup,
  HiOutlineLightBulb
} from 'react-icons/hi2';
import plantService from '../../services/plantService';

const STEPS = [
  { id: 1, label: 'First Incident Report', icon: HiOutlineExclamationTriangle },
  { id: 2, label: 'Safety Office Review', icon: HiOutlineShieldCheck },
  { id: 3, label: 'Team Formation', icon: HiOutlineUserGroup },
  { id: 4, label: 'Detailed Investigation', icon: HiOutlineClipboardDocumentCheck },
  { id: 5, label: 'Recommendations', icon: HiOutlineLightBulb },
  { id: 6, label: 'Plant Head Approval', icon: HiOutlineShieldCheck },
  { id: 7, label: 'Cost Analysis & Closed', icon: HiOutlineCurrencyDollar }
];

const IncidentDashboard = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [plants, setPlants] = useState([]);
  const [selectedPlantId, setSelectedPlantId] = useState("");
  const [filterDateStr, setFilterDateStr] = useState("");

  const handlePlantChange = (val) => {
    setSelectedPlantId(val);

  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIncidents(mockData);
        const plantsData = await plantService.getAllPlants();
        setPlants(plantsData || []);

        // Auto-select plant if only 1 exists
        if (plantsData?.length === 1) {
          setSelectedPlantId(plantsData[0].plantId.toString());
        }
      } catch (err) {
        console.error("Error loading incident data:", err);
      }
    };
    loadData();
  }, []);

  const getIncidentsByStep = (stepId) => {
    return incidents.filter(inc => {
      // Find plant name for mock data compatibility
      const selectedPlant = plants.find(p => p.plantId.toString() === selectedPlantId);
      const plantName = selectedPlant ? selectedPlant.plantName : "";

      // Apply filters first
      if (selectedPlantId && inc.plantName !== plantName) return false;
      if (filterDateStr && !inc.incidentDate.startsWith(filterDateStr)) return false;

      // Step 7 represents both Cost Analysis and formally Closed incidents
      if (stepId === 7 && inc.currentStep >= 7) return true;
      return inc.currentStep === stepId;
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Injury': return 'bg-red-100 text-red-800 border-red-200';
      case 'Equipment': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Environment': return 'bg-green-100 text-green-800 border-green-200';
      case 'Nearmiss': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Road': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="pagecontainer">
      <div className='page-header'>
        <div className='header-contain'>
          <h1>Incident Summary</h1>
          <p>Manage and track incidents through the investigation lifecycle</p>
        </div>
        <div className="header-actions gap-10">
          <select
            className="rounded-lg mr-2 border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={selectedPlantId}
            onChange={(e) => handlePlantChange(e.target.value)}
          >
            <option value="">All Plants</option>
            {plants.map(p => (
              <option key={p.plantId} value={p.plantId}>{p.plantName}</option>
            ))}
          </select>

          <input
            type="date"
            className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={filterDateStr}
            onChange={(e) => setFilterDateStr(e.target.value)}
            title="Filter by incident date"
          />

          <button
            onClick={() => navigate('/home/incident/new')}
            className="btn btn-primary"
          >
            <HiOutlineExclamationTriangle />
            Report New Incident
          </button>
        </div>
      </div>
      {/* <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary tracking-tight">Incident Summary</h1>
          <p className="text-tertiary mt-1">Manage and track incidents through the investigation lifecycle</p>
        </div>
        
        <div className="flex items-center gap-4 flex-wrap">
          <select 
            className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={filterPlant}
            onChange={(e) => setFilterPlant(e.target.value)}
          >
            <option value="">All Plants</option>
            <option value="Plant A">Plant A</option>
            <option value="Plant B">Plant B</option>
            <option value="Plant 1">Plant 1</option>
            <option value="Plant 2">Plant 2</option>
          </select>

          <input 
            type="date"
            className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={filterDateStr}
            onChange={(e) => setFilterDateStr(e.target.value)}
            title="Filter by incident date"
          />

          <button 
            onClick={() => navigate('/home/incident/new')}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-colors flex items-center gap-2"
          >
            <HiOutlineExclamationTriangle className="text-lg" />
            Report New Incident
          </button>
        </div>
      </div> */}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-card p-4 rounded-xl  border-l-[3px] border-blue-500 shadow-sm flex items-center">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg mr-4">
            <HiOutlineExclamationTriangle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Total Incidents</p>
            <p className="text-2xl font-bold text-primary">
              {incidents.filter(inc => {
                const selectedPlant = plants.find(p => p.plantId.toString() === selectedPlantId);
                const plantName = selectedPlant ? selectedPlant.plantName : "";
                return (!selectedPlantId || inc.plantName === plantName) && (!filterDateStr || inc.incidentDate.startsWith(filterDateStr));
              }).length}
            </p>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl  border-l-[3px] border-yellow-500 shadow-sm flex items-center">
          <div className="p-3 bg-yellow-50 text-yellow-600 rounded-lg mr-4">
            <HiOutlineShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Pending Review (Step 2/6)</p>
            <p className="text-2xl font-bold text-primary">
              {incidents.filter(i => {
                const selectedPlant = plants.find(p => p.plantId.toString() === selectedPlantId);
                const plantName = selectedPlant ? selectedPlant.plantName : "";
                return (!selectedPlantId || i.plantName === plantName) && (!filterDateStr || i.incidentDate.startsWith(filterDateStr)) && (i.currentStep === 2 || i.currentStep === 6);
              }).length}
            </p>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl  border-l-[3px] border-purple-500 shadow-sm flex items-center">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-lg mr-4">
            <HiOutlineClipboardDocumentCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Open Investigations (Step 4)</p>
            <p className="text-2xl font-bold text-primary">
              {incidents.filter(i => {
                const selectedPlant = plants.find(p => p.plantId.toString() === selectedPlantId);
                const plantName = selectedPlant ? selectedPlant.plantName : "";
                return (!selectedPlantId || i.plantName === plantName) && (!filterDateStr || i.incidentDate.startsWith(filterDateStr)) && i.currentStep === 4;
              }).length}
            </p>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl  border-l-[3px] border-green-500 shadow-sm flex items-center">
          <div className="p-3 bg-green-50 text-green-600 rounded-lg mr-4">
            <HiOutlineCurrencyDollar className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Closed</p>
            <p className="text-2xl font-bold text-primary">
              {incidents.filter(i => {
                const selectedPlant = plants.find(p => p.plantId.toString() === selectedPlantId);
                const plantName = selectedPlant ? selectedPlant.plantName : "";
                return (!selectedPlantId || i.plantName === plantName) && (!filterDateStr || i.incidentDate.startsWith(filterDateStr)) && i.currentStep === 7;
              }).length}
            </p>
          </div>
        </div>
      </div>

      {/* Kanban Board style view */}
      {/* <h2>Investigation Pipeline</h2>
      <div className="flex bg-card gap-4 overflow-x-auto pb-4 h-[calc(100vh-280px)] items-start pt-2 px-1">
        {STEPS.map(step => {
          const stepIncidents = getIncidentsByStep(step.id);
          const StepIcon = step.icon;

          return (
            <div key={step.id} className="min-w-[320px] w-[320px] bg-gray-100/50 rounded-xl border border-gray-200 flex flex-col max-h-full">
             
              <div className="p-3 bg-card  border-b border-gray-200 rounded-t-xl flex items-center justify-between sticky top-0 shadow-sm z-10">
                <div className="flex items-center gap-2">
                  <div className="bg-gray-100 p-1.5 rounded-md text-secondary">
                    <StepIcon className="w-4 h-4" />
                  </div>
                  <h3 className="font-semibold text-primary text-base mb-0">
                    {step.id}. {step.label}
                  </h3>
                </div>
                <span className="bg-gray-200 text-secondary text-base font-bold px-2 py-0.5 rounded-full">
                  {stepIncidents.length}
                </span>
              </div>

              
              <div className="p-3 overflow-y-auto flex-1 flex flex-col gap-3">
                {stepIncidents.map(incident => (
                  <div
                    key={incident.id}
                    onClick={() => navigate(`/home/incident/${incident.id}`)}
                    className="bg-card p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 cursor-pointer transition-all duration-200 group relative overflow-hidden"
                  >
                   
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-300 group-hover:bg-blue-500 transition-colors"></div>

                    <div className="pl-2">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-base font-bold text-tertiary tracking-wider">
                          {incident.incidentNumber}
                        </span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border tracking-wider ${getTypeColor(incident.type)}`}>
                          {incident.type}
                        </span>
                      </div>

                      <h4 className="font-semibold text-primary text-base mb-1 leading-snug line-clamp-2">
                        {incident.title}
                      </h4>

                      <div className="flex items-center gap-2 mt-3 text-base text-tertiary">
                        <span>{new Date(incident.incidentDate).toLocaleDateString()}</span>
                        <span>•</span>
                        <span className="truncate">{incident.plantName}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {stepIncidents.length === 0 && (
                  <div className="p-4 text-center rounded-lg border border-dashed border-gray-300 text-gray-400 text-base">
                    No incidents at this stage
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div> */}
      <h2 className="text-sm font-semibold text-primary mb-1">Investigation Pipeline</h2>
      <p className="text-xs text-gray-400 mb-4">Track incidents across each stage of the workflow</p>

      <div className="flex gap-4 overflow-x-auto pb-4 h-[calc(100vh-280px)] items-start pt-1 px-1">
        {STEPS.map((step, index) => {
          const stepIncidents = getIncidentsByStep(step.id);
          const StepIcon = step.icon;

          const stepColors = [
            {
              dot: 'bg-slate-400',
              topBorder: 'border-t-slate-400',
              badge: 'bg-slate-100 text-slate-500',
            },
            {
              dot: 'bg-blue-500',
              topBorder: 'border-t-blue-500',
              badge: 'bg-blue-50 text-blue-600',
            },
            {
              dot: 'bg-violet-500',
              topBorder: 'border-t-violet-500',
              badge: 'bg-violet-50 text-violet-600',
            },
            {
              dot: 'bg-orange-500',
              topBorder: 'border-t-orange-500',
              badge: 'bg-orange-50 text-orange-600',
            },
            {
              dot: 'bg-emerald-500',
              topBorder: 'border-t-emerald-500',
              badge: 'bg-emerald-50 text-emerald-600',
            },
            {
              dot: 'bg-rose-500',
              topBorder: 'border-t-rose-500',
              badge: 'bg-rose-50 text-rose-600',
            },
          ];

          const color = stepColors[index % stepColors.length];

          return (
            <div
              key={step.id}
              className="min-w-[300px] w-[300px] bg-card rounded-xl border border-gray-200 flex flex-col max-h-full shadow-sm"
            >
              {/* Column Header */}
              <div
                className={`p-3 bg-gray-50 border-b border-gray-200 rounded-t-xl border-t-[3px] ${color.topBorder} flex items-center justify-between sticky top-0 z-10`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${color.dot} flex-shrink-0`} />
                  <h3 className="font-semibold text-primary text-xs mb-0">
                    {step.id}. {step.label}
                  </h3>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${color.badge}`}>
                  {stepIncidents.length}
                </span>
              </div>

              {/* Column Cards */}
              <div className="p-3 overflow-y-auto flex-1 flex flex-col gap-2.5">
                {stepIncidents.map(incident => (
                  <div
                    key={incident.id}
                    onClick={() => navigate(`/home/incident/${incident.id}`)}
                    className="bg-page-bg hover:bg-card p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md cursor-pointer transition-all duration-200"
                  >
                    {/* ID + Badge */}
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-[9.5px] font-semibold text-gray-400 tracking-wide uppercase">
                        {incident.incidentNumber}
                      </span>
                      <span className={`text-[8.5px] uppercase font-bold px-2 py-0.5 rounded tracking-wider ${getTypeColor(incident.type)}`}>
                        {incident.type}
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="font-medium text-primary text-xs leading-snug line-clamp-2 mb-2">
                      {incident.title}
                    </h4>

                    {/* Meta */}
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                      <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <rect x="2" y="2" width="12" height="12" rx="2" />
                        <line x1="2" y1="6" x2="14" y2="6" />
                      </svg>
                      <span>{new Date(incident.incidentDate).toLocaleDateString()}</span>
                      <span className="text-gray-300">·</span>
                      <span className="truncate">{incident.plantName}</span>
                    </div>
                  </div>
                ))}

                {stepIncidents.length === 0 && (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 p-6 rounded-lg border border-dashed border-gray-200 text-gray-400 text-xs text-center">
                    <svg className="w-5 h-5 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                    No incidents at this stage
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IncidentDashboard;
